'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';

import styles from '@/app/[locale]/ai-model/ai-models.module.css';
import ModelMark from '@/components/ai-models/ModelMark';
import {
  buildComparisonPath,
  formatTokenCount,
  formatUsdPrice,
  localizedModelPath,
  type ModelDirectoryItem,
} from '@/libs/ai-models';
import { getAiModelsCopy } from '@/libs/ai-models-i18n';

export default function ModelDirectoryGrid({ models, locale }: { models: ModelDirectoryItem[]; locale: string }) {
  const copy = getAiModelsCopy(locale);
  const [selected, setSelected] = useState<string[]>([]);
  const selectedModels = useMemo(
    () => selected.flatMap((slug) => models.find((model) => model.slug === slug) || []),
    [models, selected],
  );

  const toggle = (slug: string) => {
    setSelected((current) => {
      if (current.includes(slug)) return current.filter((item) => item !== slug);
      if (current.length >= 2) return [current[1], slug];
      return [...current, slug];
    });
  };

  return (
    <>
      <div className={styles.modelGrid}>
        {models.map((model) => {
          const isSelected = selected.includes(model.slug);
          return (
            <article className={`${styles.modelCard} ${isSelected ? styles.modelCardSelected : ''}`} key={model.slug}>
              <span className={styles.cardAccent} style={{ backgroundColor: model.accent }} aria-hidden="true" />
              <div className={styles.cardTop}>
                <div className={styles.cardIdentity}>
                  <ModelMark model={model} />
                  <div>
                    <p>{model.organization}</p>
                    <h3><Link href={localizedModelPath(locale, `/ai-model/${model.slug}`)}>{model.name}</Link></h3>
                  </div>
                </div>
                <span className={styles.statusBadge}>{copy.statusLabels[model.status]}</span>
              </div>

              <p className={styles.cardSummary}>{model.summary}</p>

              <dl className={styles.cardFacts}>
                <div><dt>{copy.contextWindow}</dt><dd>{formatTokenCount(model.contextWindow, locale)}</dd></div>
                <div><dt>{copy.inputPrice}</dt><dd>{formatUsdPrice(model.inputPrice, locale)}</dd></div>
                <div><dt>{copy.outputPrice}</dt><dd>{formatUsdPrice(model.outputPrice, locale)}</dd></div>
              </dl>

              <div className={styles.modalityList} aria-label={`${copy.filterLabels.input}: ${model.inputModalities.join(', ')}`}>
                {model.inputModalities.slice(0, 4).map((modality) => (
                  <span key={modality}>{copy.modalityLabels[modality]}</span>
                ))}
              </div>

              <div className={styles.cardActions}>
                <Link href={localizedModelPath(locale, `/ai-model/${model.slug}`)}>{copy.viewModel}</Link>
                <button
                  type="button"
                  className={isSelected ? styles.compareToggleSelected : styles.compareToggle}
                  onClick={() => toggle(model.slug)}
                  aria-pressed={isSelected}
                >
                  {isSelected ? copy.selected : copy.selectToCompare}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {selected.length > 0 && (
        <aside className={styles.selectionBar} aria-live="polite">
          <div>
            <strong>{copy.compareSelected}</strong>
            <span>{selectedModels.map((model) => model.shortName).join(' + ')}</span>
          </div>
          <div className={styles.selectionActions}>
            {selectedModels.map((model) => (
              <button type="button" onClick={() => toggle(model.slug)} key={model.slug} aria-label={`${copy.remove} ${model.name}`}>
                {model.shortName} <span aria-hidden="true">×</span>
              </button>
            ))}
            {selected.length === 2 ? (
              <Link href={buildComparisonPath(locale, selected[0], selected[1])}>{copy.compareSelected}</Link>
            ) : (
              <span className={styles.selectionHint}>2</span>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
